import {isDraft} from 'immer';
import {shallowEqual} from 'react-redux';

import * as logging from 'util/Logging';

interface MemoizedFunction {
  cacheKey: string;
  arguments: Array<unknown>;
  result: unknown;
}

interface MemoizedFunctionCache {
  [key: string]: MemoizedFunction;
}

const MAX_MEMOIZED_FUNCTION_CACHE_SIZE = 500;

export class STMethodMemoizer {
  functionCache: MemoizedFunctionCache = {};
  keyStack: Array<string> = [];
  LRAPosition: number = 0;
  maxCacheSize: number = MAX_MEMOIZED_FUNCTION_CACHE_SIZE;

  constructor(cacheSize?: number) {
    this.maxCacheSize = cacheSize ?? MAX_MEMOIZED_FUNCTION_CACHE_SIZE;
  }

  getFunctionValue<TReturn, TFunc extends (...args: Array<unknown>) => TReturn>({
    functionIdentifier,
    functionToMemoize,
    args,
    cacheKey,
  }: {
    functionIdentifier: string;
    cacheKey: string;
    functionToMemoize: TFunc;
    args: Parameters<TFunc>;
  }): TReturn {
    if (args.some((arg) => isDraft(arg))) {
      // If any of the arguments are immer drafts, we don't want to memoize the result
      return functionToMemoize(...args);
    }
    const key = `${functionIdentifier}-${cacheKey}`;
    const cachedFunction = this.functionCache[key];
    // TODO(BNCH-169128): Investigate and fix the source of the exceptions thrown here
    try {
      // Shallow compare each argument to see if they match
      let cacheMatchesArgs = cachedFunction && cachedFunction.arguments.length === args.length;
      if (cacheMatchesArgs) {
        args.forEach((argument, index) => {
          cacheMatchesArgs = cacheMatchesArgs && shallowEqual(argument, cachedFunction.arguments[index]);
        });
      }
      if (cacheMatchesArgs) {
        return cachedFunction.result as TReturn;
      } else {
        const result = functionToMemoize(...args);
        this.functionCache[key] = {cacheKey, arguments: args, result};
        if (this.keyStack.length >= this.maxCacheSize) {
          const removedKey = this.keyStack[this.LRAPosition];
          delete this.functionCache[removedKey];
          this.keyStack[this.LRAPosition] = key;
          this.LRAPosition = (this.LRAPosition + 1) % this.maxCacheSize;
        } else {
          this.keyStack.push(key);
        }
        return result;
      }
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      logging.warn('Error occurred while memoizing structured table method', {
        extra: {functionIdentifier, errorMessage: e instanceof Error ? e.message : String(e)},
      });
      return functionToMemoize(...args);
    }
  }
}
